import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Search,
  Loader2,
  Building2,
  Mail,
  Phone,
  MapPin,
  ShoppingCart,
  Coins,
  TrendingUp,
  ArrowRight,
  MessageSquare,
  Send,
  User,
  X,
} from 'lucide-react';
import api from '../lib/api/client';
import { suppliersAPI, type SupplierMessage } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

interface Customer {
  id: number;
  name: string;
  company_type: string;
  primary_email: string;
  primary_phone: string;
  city: string;
  country: string;
  po_count?: number;
  total_value?: number;
  open_lines?: number;
}

interface CustomerStats {
  total_customers: number;
  total_po_value: number;
  open_po_lines: number;
  total_pos: number;
}

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [chatCustomer, setChatCustomer] = useState<Customer | null>(null);

  const { formatCurrency: _formatCurrency } = useFormatters();
  const { user } = useAuth();
  const [supplierCurrency, setSupplierCurrency] = useState('GBP');

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        // CR-5867: the response includes a top-level `currency` field driven
        // by the supplier company's base_currency, so the symbol and the
        // converted totals stay coherent across multi-country customers.
        const response = await api.get('/core/my-customers/');

        const customersData = response.data.results || [];
        if (response.data.currency) {
          setSupplierCurrency(response.data.currency);
        }
        setCustomers(customersData);

        const totalStats: CustomerStats = {
          total_customers: customersData.length,
          total_po_value: customersData.reduce((sum: number, c: Customer) => sum + (c.total_value || 0), 0),
          open_po_lines: customersData.reduce((sum: number, c: Customer) => sum + (c.open_lines || 0), 0),
          total_pos: customersData.reduce((sum: number, c: Customer) => sum + (c.po_count || 0), 0),
        };
        setStats(totalStats);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load customers';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.country?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const CURRENCY_SYMBOLS: Record<string, string> = {
    GBP: '£', USD: '$', EUR: '€', PLN: 'PLN ', CAD: 'CA$', AUD: 'A$',
  };
  const formatCurrency = (value: number) => {
    const symbol = CURRENCY_SYMBOLS[supplierCurrency] || supplierCurrency + ' ';
    return `${symbol}${value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading customers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Customers</h1>
          <p className="text-muted-foreground">
            Companies that purchase from your company
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Customers</p>
                  <p className="text-2xl font-bold">{stats.total_customers}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <ShoppingCart className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                  <p className="text-2xl font-bold">{stats.total_pos}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Coins className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Order Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.total_po_value)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Open Lines</p>
                  <p className="text-2xl font-bold">{stats.open_po_lines}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search customers by name, city, or country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customer List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCustomers.map((customer) => (
          <Card key={customer.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    {/* CR-6250: the name opens the customer's profile. A real
                        button, matching the Organisation Structure node, so it
                        is keyboard reachable. */}
                    <CardTitle className="text-lg">
                      <button
                        type="button"
                        className="text-left hover:text-primary hover:underline"
                        title={`Open ${customer.name}`}
                        onClick={() => navigate(`/companies/${customer.id}`)}
                      >
                        {customer.name}
                      </button>
                    </CardTitle>
                    <Badge variant="outline" className="mt-1">
                      Customer
                    </Badge>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {customer.primary_email && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <span className="truncate">{customer.primary_email}</span>
                </div>
              )}
              {customer.primary_phone && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>{customer.primary_phone}</span>
                </div>
              )}
              {(customer.city || customer.country) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {[customer.city, customer.country].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}

              <div className="pt-3 border-t flex items-center justify-between">
                <div className="text-sm">
                  <span className="text-muted-foreground">Orders: </span>
                  <span className="font-medium">{customer.po_count || 0}</span>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setChatCustomer(customer)}
                    className="text-primary"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Messages
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/purchasing?customer=${customer.id}`)}
                    className="text-primary"
                  >
                    View Orders
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCustomers.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery
                ? 'No customers found matching your search.'
                : 'No customers found.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Messages Dialog */}
      {chatCustomer && user?.company && (
        <CustomerChatDialog
          supplierId={user.company}
          customerId={chatCustomer.id}
          customerName={chatCustomer.name}
          currentUserId={user.id}
          onClose={() => setChatCustomer(null)}
        />
      )}
    </div>
  );
}

/* ── Chat Dialog ─────────────────────────────────────────────── */

interface CustomerChatDialogProps {
  supplierId: number;
  customerId: number;
  customerName: string;
  currentUserId: number;
  onClose: () => void;
}

function CustomerChatDialog({ supplierId, customerId, customerName, currentUserId, onClose }: CustomerChatDialogProps) {
  const { formatDateTime } = useFormatters();
  const [messages, setMessages] = useState<SupplierMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const res = await suppliersAPI.getMessages({ supplier: supplierId, customer: customerId, page_size: 100 });
      setMessages(res.data.results || []);
    } catch (err: unknown) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    try {
      setSending(true);
      const res = await suppliersAPI.sendMessage({ supplier: supplierId, customer: customerId, content: newMessage.trim() });
      setMessages((prev) => [...prev, res.data]);
      setNewMessage('');
    } catch (err: unknown) {
      console.error('Failed to send:', err);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const formatTime = (ts: string) => formatDateTime(ts);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">Messages — {customerName}</h2>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[250px]">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <p>No messages yet. Start the conversation.</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isMe = msg.sender === currentUserId;
              return (
                <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    {!isMe && <User className="h-3 w-3 text-muted-foreground" />}
                    <span className="text-xs font-medium">{isMe ? 'You' : msg.sender_name}</span>
                    <span className="text-[10px] text-muted-foreground">{formatTime(msg.created_at)}</span>
                  </div>
                  <div className={`rounded-lg px-3 py-2 max-w-[85%] text-sm ${isMe ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    {msg.content}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={2}
            maxLength={2000}
            className="w-full text-sm border rounded-md px-3 py-2 resize-none bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <div className="flex justify-end mt-2">
            <Button size="sm" disabled={!newMessage.trim() || sending} onClick={handleSend}>
              {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
